import { Log } from 'ng2-logger';
import MockAPIClient from '../services/mock-api-client';

const L = Log.create('AuthValidator');

export class AuthValidator {
  private static _instance: AuthValidator;
  private _apiClient: MockAPIClient;

  constructor() {
    this._apiClient = new MockAPIClient();
  }

  static getInstance(): AuthValidator {
    if (!AuthValidator._instance) {
      AuthValidator._instance = new AuthValidator();
    }
    return AuthValidator._instance;
  }

  /**
   * Test cookie validation and log results to console
   * Note: HTTP-only cookies cannot be accessed via JavaScript
   */
  async testCookieValidation(): Promise<void> {
    console.group('🔐 Cookie Authentication Test');

    try {
      const result = await this._apiClient.ValidateCookieAuth();

      if (result.isValid) {
        console.log('✅ Cookie is VALID');
        console.log('👤 User Info:', result.user);
        console.log('📧 Email:', result.user?.email);
        console.log('👥 Role:', result.user?.role);
        console.log('🆔 User ID:', result.user?.userId);
        console.log('🔒 Note: HTTP-only cookie detected and validated successfully');
      } else {
        console.log('❌ Cookie is INVALID');
        console.log('🚫 Error:', result.error);
      }
    } catch (error) {
      console.error('💥 Test failed:', error);
    }

    console.groupEnd();
  }

  /**
   * Test overall authentication status
   */
  async testAuthStatus(): Promise<void> {
    console.group('🔍 Authentication Status Test');

    try {
      const result = await this._apiClient.GetAuthStatus();

      console.log('🔐 Authenticated:', result.authenticated);
      console.log('🔧 Method:', result.method);

      if (result.authenticated && result.user) {
        console.log('👤 User Info:', result.user);
        console.log('📧 Email:', result.user?.email);
        console.log('👥 Role:', result.user?.role);
        console.log('🆔 User ID:', result.user?.userId);

        if (result.method === 'cookie') {
          console.log('🔒 Using HTTP-only cookie authentication');
        }
      } else if (result.error) {
        console.log('🚫 Error:', result.error);
      }
    } catch (error) {
      console.error('💥 Test failed:', error);
    }

    console.groupEnd();
  }

  /**
   * Check what cookies are available (only non-HTTP-only cookies)
   */
  checkCookies(): void {
    console.group('🍪 Cookie Information');

    const cookies = document.cookie.split(';');
    console.log('📋 JavaScript-accessible cookies:', cookies);
    console.log('⚠️  Note: HTTP-only cookies (like ap_access_token) are not visible here');

    const hasVisibleAuthCookie = document.cookie.includes('ap_access_token');
    console.log('🔑 ap_access_token visible to JavaScript:', hasVisibleAuthCookie);

    if (!hasVisibleAuthCookie) {
      console.log(
        '🔒 This is expected for HTTP-only cookies - they are hidden from JavaScript for security',
      );
      console.log(
        '💡 To test if the HTTP-only cookie exists, use testCookieValidation() or testAuthStatus()',
      );
    }

    console.groupEnd();
  }

  /**
   * Test if HTTP-only cookie exists by making an API call
   */
  async testHttpOnlyCookie(): Promise<void> {
    console.group('🔒 HTTP-Only Cookie Test');

    try {
      console.log('🚀 Testing HTTP-only cookie by calling /integration/authuser...');

      const response = await this._apiClient.GetResponse(this._apiClient.GetAuthUser());

      if (response.status === 200) {
        const content = await this._apiClient.GetContent(response);
        console.log('✅ HTTP-only cookie is present and valid');
        console.log('👤 User authenticated via HTTP-only cookie:', content);
      } else if (response.status === 401) {
        console.log('❌ HTTP-only cookie is missing, invalid, or expired');
        console.log('🔄 You may need to log in again');
      } else {
        console.log(`⚠️  Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error('💥 HTTP-only cookie test failed:', error);
    }

    console.groupEnd();
  }

  /**
   * Run all tests
   */
  async runAllTests(): Promise<void> {
    console.log('🚀 Starting Authentication Tests...');

    this.checkCookies();
    await this.testAuthStatus();

    // Only test cookie validation if using constructor_idp
    const { appSettings } = window as any;
    if (appSettings?.CONSTRUCTOR_IDP) {
      await this.testCookieValidation();
      await this.testHttpOnlyCookie();
    } else {
      console.log('ℹ️ Skipping cookie tests - not using constructor_idp');
    }

    console.log('✨ Authentication tests completed!');
  }
}

// Global helper functions for easy console access
(window as any).testAuth = () => {
  const validator = AuthValidator.getInstance();
  validator.runAllTests();
};

(window as any).testCookie = () => {
  const validator = AuthValidator.getInstance();
  validator.testCookieValidation();
};

(window as any).testHttpOnlyCookie = () => {
  const validator = AuthValidator.getInstance();
  validator.testHttpOnlyCookie();
};

(window as any).checkCookies = () => {
  const validator = AuthValidator.getInstance();
  validator.checkCookies();
};

export default AuthValidator;
